
import React, { useState, useEffect } from "react";
import API from "../services/api";
import { Line } from "react-chartjs-2";
import * as XLSX from "xlsx";
import "chart.js/auto";
import "./GraphPage.css";

// ------------------------------------------------------
// Timezone utilities
// All times displayed to the user follow the browser's
// local timezone. Database timestamps remain in UTC.
// ------------------------------------------------------

const pad = (num) => String(num).padStart(2, "0");

const LOCAL_TIMEZONE =
    Intl.DateTimeFormat().resolvedOptions().timeZone ||
    "Browser local time";

// Format a Date for an HTML datetime-local input.
const formatDateTimeLocal = (date) => {
    return (
        `${date.getFullYear()}-` +
        `${pad(date.getMonth() + 1)}-` +
        `${pad(date.getDate())}T` +
        `${pad(date.getHours())}:` +
        `${pad(date.getMinutes())}`
    );
};

// Parse API timestamps as actual instants.
// Legacy timestamps without a timezone are assumed UTC.
const parseAPITimestamp = (timestamp) => {
    if (!timestamp) return null;

    let value = String(timestamp).trim();

    value = value.replace(" ", "T");

    if (!/(Z|[+-]\d{2}:?\d{2})$/i.test(value)) {
        value += "Z";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return null;
    }

    return date;
};

// Full timestamp in the visitor's timezone.
const formatLocalTime = (timestamp) => {
    const date = parseAPITimestamp(timestamp);

    if (!date) return "N/A";

    return date.toLocaleString("en-GB", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hourCycle: "h23",
        timeZoneName: "shortOffset",
    });
};

// Clock time for chart labels.
const formatLocalClock = (timestamp) => {
    const date = parseAPITimestamp(timestamp);

    if (!date) return "";

    return date.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hourCycle: "h23",
    });
};

// Convert datetime-local to an actual instant.
//
// new Date("YYYY-MM-DDTHH:mm") interprets the input in
// the browser's own timezone.
//
// Reject nonexistent local times during DST transitions.
// Also reject ambiguous repeated local times instead of
// silently choosing one of two possible instants.
const localInputToDate = (value) => {
    if (!value) {
        throw new Error("Please select a date and time.");
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        throw new Error("Invalid date or time.");
    }

    const normalized = formatDateTimeLocal(date);

    if (normalized !== value.slice(0, 16)) {
        throw new Error(
            "This local time does not exist because of a " +
            "daylight-saving time change."
        );
    }

    // Check nearby instants for a second occurrence
    // of the same local wall-clock time.
    for (let minutes = -180; minutes <= 180; minutes += 15) {
        if (minutes === 0) continue;

        const candidate = new Date(
            date.getTime() + minutes * 60 * 1000
        );

        if (formatDateTimeLocal(candidate) === normalized) {
            throw new Error(
                "This local time occurs more than once " +
                "because of a daylight-saving time change. " +
                "Please choose another time range."
            );
        }
    }

    return date;
};

// Django's existing history API interprets naive
// start_time/end_time parameters as UTC.
// Send UTC clock digits without an offset.
const toDjangoUTC = (date) => {
    return date.toISOString().slice(0, 19);
};

// Get midnight in the visitor's local timezone.
const localMidnight = (date) => {
    const result = new Date(date);
    result.setHours(0, 0, 0, 0);
    return result;
};

// Move calendar days in the visitor's timezone.
// This respects daylight-saving changes.
const shiftLocalDay = (date, days) => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
};

// Unique UTC second key for Excel grouping.
//
// Do not group by local clock digits:
// During a DST rollback two different UTC instants
// may have the same local time.
const toUTCSecondKey = (timestamp) => {
    const date = parseAPITimestamp(timestamp);

    if (!date) return null;

    return date.toISOString().slice(0, 19);
};

// ------------------------------------------------------
// Graph page
// ------------------------------------------------------

const GraphPage = () => {
    const [startTime, setStartTime] = useState("");
    const [endTime, setEndTime] = useState("");
    const [tags, setTags] = useState([]);
    const [loading, setLoading] = useState(false);

    // Each selected tag has its own chart.
    const [selectedCharts, setSelectedCharts] = useState([
        {
            id: Date.now(),
            tagId: "",
            tagName: "",
            chartData: null,
            tableData: [],
        },
    ]);

    // --------------------------------------------------
    // Load tags
    // --------------------------------------------------

    useEffect(() => {
        const fetchTags = async () => {
            try {
                const response = await API.get("/tags/");
                setTags(response.data);
            } catch (error) {
                console.error(
                    "Error fetching tags:",
                    error
                );
            }
        };

        fetchTags();
    }, []);

    // --------------------------------------------------
    // Quick time selection
    // --------------------------------------------------

    const setQuickTimeRange = (range) => {
        const now = new Date();

        let newStartTime;
        let newEndTime;

        switch (range) {
            case "last_hour":
                newStartTime = new Date(
                    now.getTime() - 60 * 60 * 1000
                );

                newEndTime = now;
                break;

            case "current_4_hours":
                newStartTime = new Date(
                    now.getTime() - 4 * 60 * 60 * 1000
                );

                newEndTime = now;
                break;

            case "last_10_hours":
                newStartTime = new Date(
                    now.getTime() - 10 * 60 * 60 * 1000
                );

                newEndTime = now;
                break;

            case "current_day":
                newStartTime = localMidnight(now);
                newEndTime = now;
                break;

            case "previous_day":
                newStartTime = localMidnight(
                    shiftLocalDay(now, -1)
                );

                newEndTime = new Date(
                    localMidnight(now).getTime() -
                    60 * 1000
                );
                break;

            default:
                return;
        }

        setStartTime(
            formatDateTimeLocal(newStartTime)
        );

        setEndTime(
            formatDateTimeLocal(newEndTime)
        );
    };

    // --------------------------------------------------
    // Add a tag/chart selector
    // --------------------------------------------------

    const addNewChartSelector = () => {
        setSelectedCharts((prev) => [
            ...prev,
            {
                id: Date.now() + Math.random(),
                tagId: "",
                tagName: "",
                chartData: null,
                tableData: [],
            },
        ]);
    };

    // --------------------------------------------------
    // Remove a chart
    // --------------------------------------------------

    const removeChartSelector = (chartId) => {
        setSelectedCharts((prev) =>
            prev.filter(
                (item) => item.id !== chartId
            )
        );
    };

    // --------------------------------------------------
    // Change selected tag
    // --------------------------------------------------

    const updateChartTag = (chartId, tagId) => {
        const tag = tags.find(
            (item) =>
                String(item.id) === String(tagId)
        );

        setSelectedCharts((prev) =>
            prev.map((item) =>
                item.id === chartId
                    ? {
                          ...item,
                          tagId,
                          tagName: tag ? tag.name : "",
                          chartData: null,
                          tableData: [],
                      }
                    : item
            )
        );
    };

    // --------------------------------------------------
    // Fetch historical data
    // --------------------------------------------------

    const fetchAllTagData = async () => {
        if (!startTime || !endTime) {
            alert(
                "Please select start time and end time."
            );
            return;
        }

        const validCharts = selectedCharts.filter(
            (item) => item.tagId
        );

        if (validCharts.length === 0) {
            alert(
                "Please select at least one tag."
            );
            return;
        }

        let startUTC;
        let endUTC;

        try {
            // Inputs use the visitor's timezone.
            const startDate = localInputToDate(
                startTime
            );

            const endDate = localInputToDate(
                endTime
            );

            if (startDate >= endDate) {
                alert(
                    "End Time must be after Start Time."
                );
                return;
            }

            // Convert to UTC for the existing Django API.
            startUTC = toDjangoUTC(startDate);
            endUTC = toDjangoUTC(endDate);
        } catch (error) {
            alert(error.message);
            return;
        }

        setLoading(true);

        try {
            const updatedCharts = await Promise.all(
                validCharts.map(async (chartItem) => {
                    const response = await API.get(
                        `/tag-history/${chartItem.tagId}/`,
                        {
                            params: {
                                start_time: startUTC,
                                end_time: endUTC,
                            },
                        }
                    );

                    const data = response.data;

                    return {
                        ...chartItem,
                        tableData: data,
                        chartData: {
                            labels: data.map((item) =>
                                formatLocalClock(
                                    item.timestamp
                                )
                            ),
                            datasets: [
                                {
                                    label:
                                        chartItem.tagName ||
                                        `Tag ${chartItem.tagId}`,

                                    data: data.map(
                                        (item) => item.value
                                    ),

                                    borderWidth: 2,
                                    tension: 0.3,
                                    pointRadius: 3,
                                },
                            ],
                        },
                    };
                })
            );

            // Preserve empty chart selectors.
            setSelectedCharts((prev) =>
                prev.map((oldItem) => {
                    const updated =
                        updatedCharts.find(
                            (item) =>
                                item.id === oldItem.id
                        );

                    return updated || oldItem;
                })
            );
        } catch (error) {
            console.error(
                "Error fetching tag history:",
                error.response
                    ? error.response.data
                    : error.message
            );

            alert(
                "Error fetching one or more charts."
            );
        } finally {
            setLoading(false);
        }
    };

    // --------------------------------------------------
    // Excel export
    // --------------------------------------------------

    const downloadExcel = () => {
        const chartsWithData =
            selectedCharts.filter(
                (chart) =>
                    chart.tagName &&
                    chart.tableData &&
                    chart.tableData.length > 0
            );

        if (chartsWithData.length === 0) {
            alert(
                "No data available for export!"
            );
            return;
        }

        const groupedByTimestamp = {};

        chartsWithData.forEach((chart) => {
            chart.tableData.forEach((record) => {
                const secondKey =
                    toUTCSecondKey(
                        record.timestamp
                    );

                if (!secondKey) return;

                if (
                    !groupedByTimestamp[secondKey]
                ) {
                    groupedByTimestamp[secondKey] = {
                        timestamp: formatLocalTime(
                            record.timestamp
                        ),
                    };
                }

                groupedByTimestamp[secondKey][
                    chart.tagName
                ] = record.value;
            });
        });

        const selectedTagNames =
            chartsWithData.map(
                (chart) => chart.tagName
            );

        // Sort by UTC instant, preserving chronological order.
        const excelRows =
            Object.keys(groupedByTimestamp)
                .sort()
                .map((timeKey) => {
                    const row = {
                        timestamp:
                            groupedByTimestamp[
                                timeKey
                            ].timestamp,
                    };

                    selectedTagNames.forEach(
                        (tagName) => {
                            row[tagName] =
                                groupedByTimestamp[
                                    timeKey
                                ][tagName];
                        }
                    );

                    return row;
                })
                .filter((row) =>
                    selectedTagNames.every(
                        (tagName) =>
                            row[tagName] !== undefined &&
                            row[tagName] !== null &&
                            row[tagName] !== ""
                    )
                );

        const worksheet =
            XLSX.utils.json_to_sheet(
                excelRows
            );

        const workbook =
            XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(
            workbook,
            worksheet,
            "Tags Data"
        );

        XLSX.writeFile(
            workbook,
            "multi_tag_grouped_by_second.xlsx"
        );
    };

    // --------------------------------------------------
    // Render
    // --------------------------------------------------

    return (
        <div className="container">
            <h1>Tag Data Graph</h1>

            <div className="quick-select">
                <button
                    onClick={() =>
                        setQuickTimeRange(
                            "last_hour"
                        )
                    }
                >
                    Last Hour
                </button>

                <button
                    onClick={() =>
                        setQuickTimeRange(
                            "current_4_hours"
                        )
                    }
                >
                    Last 4 Hours
                </button>

                <button
                    onClick={() =>
                        setQuickTimeRange(
                            "last_10_hours"
                        )
                    }
                >
                    Last 10 Hours
                </button>

                <button
                    onClick={() =>
                        setQuickTimeRange(
                            "current_day"
                        )
                    }
                >
                    Current Day
                </button>

                <button
                    onClick={() =>
                        setQuickTimeRange(
                            "previous_day"
                        )
                    }
                >
                    Previous Day
                </button>
            </div>

            <div className="form-group">
                <label>Start Time:</label>

                <input
                    type="datetime-local"
                    value={startTime}
                    onChange={(e) =>
                        setStartTime(
                            e.target.value
                        )
                    }
                />
            </div>

            <div className="form-group">
                <label>End Time:</label>

                <input
                    type="datetime-local"
                    value={endTime}
                    onChange={(e) =>
                        setEndTime(
                            e.target.value
                        )
                    }
                />
            </div>

            <p
                style={{
                    fontSize: "13px",
                    color: "#64748b",
                    marginTop: "8px",
                }}
            >
                Timezone: {LOCAL_TIMEZONE}
            </p>

            <hr />

            <h2>Select Tags</h2>

            {selectedCharts.map(
                (chart, index) => (
                    <div
                        key={chart.id}
                        className="form-group"
                        style={{
                            border:
                                "1px solid #ddd",
                            padding: "12px",
                            borderRadius: "8px",
                            marginBottom: "12px",
                        }}
                    >
                        <label>
                            Tag {index + 1}:
                        </label>

                        <select
                            value={chart.tagId}
                            onChange={(e) =>
                                updateChartTag(
                                    chart.id,
                                    e.target.value
                                )
                            }
                        >
                            <option value="">
                                Select a tag
                            </option>

                            {tags.map((tag) => (
                                <option
                                    key={tag.id}
                                    value={tag.id}
                                >
                                    {tag.name}
                                </option>
                            ))}
                        </select>

                        {selectedCharts.length >
                            1 && (
                            <button
                                onClick={() =>
                                    removeChartSelector(
                                        chart.id
                                    )
                                }
                                style={{
                                    marginLeft:
                                        "10px",
                                }}
                            >
                                Remove
                            </button>
                        )}
                    </div>
                )
            )}

            <div
                style={{
                    marginBottom: "16px",
                }}
            >
                <button
                    onClick={
                        addNewChartSelector
                    }
                >
                    + Add New Tag
                </button>
            </div>

            <button
                onClick={
                    fetchAllTagData
                }
                disabled={loading}
            >
                {loading
                    ? "Loading..."
                    : "Fetch Data"}
            </button>

            <button
                onClick={
                    downloadExcel
                }
                className="download-btn"
                style={{
                    marginLeft: "10px",
                }}
            >
                Download Excel
            </button>

            <div
                style={{
                    marginTop: "20px",
                }}
            >
                {selectedCharts.map(
                    (chart) =>
                        chart.chartData && (
                            <div
                                key={chart.id}
                                className="chart-container"
                                style={{
                                    marginBottom:
                                        "30px",
                                }}
                            >
                                <h3>
                                    {chart.tagName}
                                </h3>

                                <Line
                                    data={
                                        chart.chartData
                                    }
                                    options={{
                                        responsive: true,

                                        plugins: {
                                            tooltip: {
                                                callbacks: {
                                                    title: (
                                                        items
                                                    ) => {
                                                        if (
                                                            !items.length
                                                        ) {
                                                            return "";
                                                        }

                                                        const index =
                                                            items[0]
                                                                .dataIndex;

                                                        const record =
                                                            chart.tableData[
                                                                index
                                                            ];

                                                        return record
                                                            ? formatLocalTime(
                                                                  record.timestamp
                                                              )
                                                            : "";
                                                    },
                                                },
                                            },
                                        },

                                        scales: {
                                            x: {
                                                title: {
                                                    display:
                                                        true,
                                                    text:
                                                        "Time (Local)",
                                                },
                                            },

                                            y: {
                                                title: {
                                                    display:
                                                        true,
                                                    text:
                                                        "Value",
                                                },
                                            },
                                        },
                                    }}
                                />
                            </div>
                        )
                )}
            </div>
        </div>
    );
};

export default GraphPage;
